import React, { useState, useEffect, useCallback } from 'react';
import recruitingService from '../utils/recruitingService';
import KPICard from '../components/KPICard';
import FilterBar from '../components/FilterBar';
import HiringVelocityChart from '../components/HiringVelocityChart';
import FunnelChart from '../components/FunnelChart';
import SourceMatrix from '../components/SourceMatrix';
import RejectionChart from '../components/RejectionChart';
import JobsAtRiskTable from '../components/JobsAtRiskTable';
import './ExecutiveDashboard.css';

/**
 * ExecutiveDashboard Component
 * CPO-level recruiting analytics dashboard
 */
function ExecutiveDashboard() {
    // Filter state
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        department: null,
        jobTitle: null
    });

    // Data state
    const [summary, setSummary] = useState(null);
    const [velocity, setVelocity] = useState([]);
    const [funnel, setFunnel] = useState([]);
    const [sources, setSources] = useState([]);
    const [rejections, setRejections] = useState([]);
    const [jobsAtRisk, setJobsAtRisk] = useState([]);
    const [filterOptions, setFilterOptions] = useState({
        departments: [],
        jobTitles: []
    });

    // Loading states
    const [loading, setLoading] = useState({
        summary: true,
        velocity: true,
        funnel: true,
        sources: true,
        rejections: true,
        jobsAtRisk: true
    });

    // Error state
    const [error, setError] = useState(null);

    // Fetch filter options
    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await recruitingService.getFilterOptions();
            if (response.success) {
                setFilterOptions({
                    departments: response.data.departments || [],
                    jobTitles: response.data.jobTitles || []
                });
            }
        } catch (err) {
            console.error('Error fetching filter options:', err);
        }
    }, []);

    // Fetch all dashboard data
    const fetchDashboardData = useCallback(async () => {
        setError(null);

        // Fetch summary
        setLoading(prev => ({ ...prev, summary: true }));
        try {
            const response = await recruitingService.getExecutiveSummary(filters);
            if (response.success) {
                setSummary(response.data);
            }
        } catch (err) {
            console.error('Error fetching summary:', err);
            setError('Failed to load executive summary');
        }
        setLoading(prev => ({ ...prev, summary: false }));

        // Fetch velocity
        setLoading(prev => ({ ...prev, velocity: true }));
        try {
            const response = await recruitingService.getHiringVelocity({ months: 12 });
            if (response.success) {
                setVelocity(response.data);
            }
        } catch (err) {
            console.error('Error fetching velocity:', err);
        }
        setLoading(prev => ({ ...prev, velocity: false }));

        // Fetch funnel
        setLoading(prev => ({ ...prev, funnel: true }));
        try {
            const response = await recruitingService.getFunnelEfficiency(filters);
            if (response.success) {
                setFunnel(response.data);
            }
        } catch (err) {
            console.error('Error fetching funnel:', err);
        }
        setLoading(prev => ({ ...prev, funnel: false }));

        // Fetch sources
        setLoading(prev => ({ ...prev, sources: true }));
        try {
            const response = await recruitingService.getSourceEffectiveness({ months: 12 });
            if (response.success) {
                setSources(response.data);
            }
        } catch (err) {
            console.error('Error fetching sources:', err);
        }
        setLoading(prev => ({ ...prev, sources: false }));

        // Fetch rejections
        setLoading(prev => ({ ...prev, rejections: true }));
        try {
            const response = await recruitingService.getRejectionAnalysis(filters);
            if (response.success) {
                setRejections(response.data);
            }
        } catch (err) {
            console.error('Error fetching rejections:', err);
        }
        setLoading(prev => ({ ...prev, rejections: false }));

        // Fetch jobs at risk
        setLoading(prev => ({ ...prev, jobsAtRisk: true }));
        try {
            const response = await recruitingService.getJobsAtRisk({ limit: 20 });
            if (response.success) {
                setJobsAtRisk(response.data);
            }
        } catch (err) {
            console.error('Error fetching jobs at risk:', err);
        }
        setLoading(prev => ({ ...prev, jobsAtRisk: false }));
    }, [filters]);

    // Initial load
    useEffect(() => {
        fetchFilterOptions();
        fetchDashboardData();
    }, [fetchFilterOptions, fetchDashboardData]);

    // Handle filter changes
    const handleApplyFilters = () => {
        fetchDashboardData();
    };

    const handleResetFilters = () => {
        setFilters({
            startDate: null,
            endDate: null,
            department: null,
            jobTitle: null
        });
    };

    // Navigate to job detail (placeholder)
    const handleJobClick = (job) => {
        console.log('Navigate to job:', job.id);
        // TODO: Navigate to job detail page
        // window.location.href = `/ta/jobs?id=${job.id}`;
    };

    return (
        <div className="executive-dashboard">
            <div className="dashboard-header">
                <div className="dashboard-title-section">
                    <h1 className="dashboard-title">Recruiting Analytics</h1>
                    <p className="dashboard-subtitle">Executive Dashboard</p>
                </div>
                <div className="dashboard-actions">
                    <button className="btn-export" onClick={() => alert('Export feature coming soon')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Export Report
                    </button>
                </div>
            </div>

            {/* Filters */}
            <FilterBar
                startDate={filters.startDate}
                endDate={filters.endDate}
                department={filters.department}
                jobTitle={filters.jobTitle}
                onStartDateChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
                onEndDateChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
                onDepartmentChange={(dept) => setFilters(prev => ({ ...prev, department: dept }))}
                onJobTitleChange={(title) => setFilters(prev => ({ ...prev, jobTitle: title }))}
                departments={filterOptions.departments}
                jobTitles={filterOptions.jobTitles}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
            />

            {error && (
                <div className="dashboard-error">
                    <p>{error}</p>
                    <button onClick={fetchDashboardData}>Retry</button>
                </div>
            )}

            {/* KPI Cards Row */}
            <div className="kpi-grid">
                <KPICard
                    title="Hires YTD"
                    value={summary?.hiresYTD?.value || 0}
                    trend={summary?.hiresYTD?.trend}
                    trendValue={summary?.hiresYTD?.yoyChange}
                    subtitle={`vs ${summary?.hiresYTD?.previousYear || 0} last year`}
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="8.5" cy="7" r="4"></circle>
                            <line x1="20" y1="8" x2="20" y2="14"></line>
                            <line x1="23" y1="11" x2="17" y2="11"></line>
                        </svg>
                    }
                />
                <KPICard
                    title="Avg Time-to-Hire"
                    value={`${summary?.avgTimeToHire?.value || 0} days`}
                    status={summary?.avgTimeToHire?.status}
                    subtitle={summary?.avgTimeToHire?.status === 'good' ? 'On target' :
                             summary?.avgTimeToHire?.status === 'warning' ? 'Needs attention' : 'Critical'}
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                    }
                />
                <KPICard
                    title="Offer Acceptance Rate"
                    value={`${summary?.offerAcceptanceRate?.value || 0}%`}
                    status={summary?.offerAcceptanceRate?.status}
                    subtitle={summary?.offerAcceptanceRate?.status === 'good' ? 'Excellent' :
                             summary?.offerAcceptanceRate?.status === 'warning' ? 'Below target' : 'Needs improvement'}
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    }
                />
                <KPICard
                    title="Pipeline Health"
                    value={`${summary?.pipelineHealth?.score || 0}/100`}
                    status={
                        (summary?.pipelineHealth?.score || 0) >= 75 ? 'good' :
                        (summary?.pipelineHealth?.score || 0) >= 50 ? 'warning' : 'danger'
                    }
                    subtitle="Composite score"
                    loading={loading.summary}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="20" x2="18" y2="10"></line>
                            <line x1="12" y1="20" x2="12" y2="4"></line>
                            <line x1="6" y1="20" x2="6" y2="14"></line>
                        </svg>
                    }
                />
                <KPICard
                    title="Jobs at Risk"
                    value={summary?.jobsAtRisk?.count || 0}
                    status={(summary?.jobsAtRisk?.count || 0) > 0 ? 'danger' : 'good'}
                    subtitle={`Open >45 days with <3 candidates`}
                    loading={loading.summary}
                    onClick={() => document.getElementById('jobs-at-risk')?.scrollIntoView({ behavior: 'smooth' })}
                    icon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                    }
                />
            </div>

            {/* Charts Row 1 */}
            <div className="charts-grid charts-grid-2">
                <HiringVelocityChart
                    data={velocity}
                    loading={loading.velocity}
                />
                <FunnelChart
                    data={funnel}
                    loading={loading.funnel}
                />
            </div>

            {/* Charts Row 2 */}
            <div className="charts-grid charts-grid-2">
                <SourceMatrix
                    data={sources}
                    loading={loading.sources}
                />
                <RejectionChart
                    data={rejections}
                    loading={loading.rejections}
                />
            </div>

            {/* Jobs at Risk Table */}
            <div id="jobs-at-risk" className="table-section">
                <JobsAtRiskTable
                    data={jobsAtRisk}
                    loading={loading.jobsAtRisk}
                    onJobClick={handleJobClick}
                />
            </div>
        </div>
    );
}

export default ExecutiveDashboard;
